import type { ReactElement } from 'react';

import { AlertBanner } from './AlertBanner';
import { getGeneralizedErrors } from './helpers';
import type { IValidationError, IValidationResult } from './types';
import './ValidationAlert.scss';

export interface IValidationErrorWithElements extends IValidationError {
    validationElements?: ReactElement;
}

export interface IValidationAlert {
    defaultHeading?: string;
    validationError: IValidationErrorWithElements;
}

/**
 * displays section level validation error block
 */
export function ValidationAlert({ validationError, defaultHeading }: IValidationAlert): ReactElement | null {
    const heading = validationError.heading || defaultHeading;

    return validationError ? (
        <AlertBanner iconName="exclamation-circle-fill" header={heading}>
            {validationError.field && `[${validationError.field}]`}
            {validationError.message}
            {validationError.validationElements}
        </AlertBanner>
    ) : null;
}

export type ValidationResult = {
    validation: IValidationResult;
    validationErrors?: never;
};

export type ValidationErrors = {
    validation?: never;
    validationErrors: IValidationErrorWithElements[];
};

export type ValidationAlertsProps = {
    defaultHeading?: string;
} & (ValidationResult | ValidationErrors);

export function ValidationAlerts({
    defaultHeading,
    validation: validationResult,
    validationErrors,
}: ValidationAlertsProps): ReactElement[] {
    const errors = validationResult ? getGeneralizedErrors(validationResult) : validationErrors;

    return errors
        ?.filter(err => !err.isHidden)
        .map(err => (
            <ValidationAlert
                key={`alert|${err.code}|${err.field}|${err.heading}|${err.message}`}
                validationError={err}
                defaultHeading={err.hideDefaultHeader ? '' : defaultHeading}
            />
        ));
}
